import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { services, serviceGroups } from "../data/services";
import CTABanner from "../components/CTABanner";
import EkselensMark from "../components/EkselensMark";
import PortfolioRadar from "../components/PortfolioRadar";
import WelcomeBack from "../components/WelcomeBack";

const byslug = (slug) => services.find((s) => s.slug === slug);

const Services = () => {
  return (
    <>
      <WelcomeBack />
      {/* HERO */}
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Services</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl mt-4 text-white max-w-4xl leading-tight" data-testid="services-page-headline">
            Consulting services designed to turn transformation ambition into measurable delivery.
          </h1>
          <p className="text-zinc-200 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Ekselens combines technology insight, project and programme management, business change, service improvement, supplier optimisation, data insight and benefits realisation into one integrated consulting approach.
          </p>
        </div>
      </section>

      {/* PORTFOLIO RADAR — twelve sub-brands, each a clickable self-assessment */}
      <section className="bg-[#0B0B0C] text-white relative overflow-hidden border-t border-[#7737F6]/20" data-testid="portfolio-radar-section">
        <div className="ek-star-watermark-left opacity-40" />
        <div className="ek-container py-16 md:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow text-[#A179FA]">EKSELENS360 portfolio</div>
              <h2 className="ek-heading text-3xl md:text-4xl lg:text-5xl mt-4 text-white leading-tight">
                Twelve sub-brands.<br />One maturity engine.
              </h2>
              <p className="text-zinc-300 font-light text-base md:text-lg leading-relaxed mt-6 max-w-xl">
                Every Ekselens consulting service is anchored on the same calibrated rubric. Click any sub-brand on the radar to take a free three-minute self-assessment — you'll leave with an honest maturity score and the three improvement moves we'd talk to you about first.
              </p>
              <div className="mt-7 flex flex-wrap gap-3 text-[12px] text-zinc-400">
                <span className="px-3 py-1.5 border border-[#7737F6]/30 bg-[#7737F6]/10 text-[#A179FA] tracking-[0.18em] uppercase font-semibold">3 min</span>
                <span className="px-3 py-1.5 border border-zinc-700">Instant radar chart</span>
                <span className="px-3 py-1.5 border border-zinc-700">Top three improvement moves</span>
                <span className="px-3 py-1.5 border border-zinc-700">No login required</span>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="max-w-[760px] mx-auto">
                <PortfolioRadar />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE GROUPS */}
      <section className="bg-[#F2F2F3] ek-section">
        <div className="ek-container space-y-24">
          {serviceGroups.map((group, gi) => (
            <div key={group.id} data-testid={`service-group-${group.id}`}>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
                <div className="lg:col-span-5">
                  <div className="ek-eyebrow">0{gi + 1} · Group</div>
                  <h2 className="ek-heading text-3xl md:text-4xl mt-3 text-black">{group.title}</h2>
                </div>
                <div className="lg:col-span-7 flex lg:items-end">
                  <p className="ek-body text-lg max-w-2xl">{group.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {group.services.map((slug) => {
                  const s = byslug(slug);
                  if (!s) return null;
                  return (
                    <Link
                      key={slug}
                      to={`/services/${slug}`}
                      className="ek-card block group"
                      data-testid={`service-group-card-${slug}`}
                    >
                      <div className="flex items-start justify-between mb-5">
                        <EkselensMark size={20} color="#7737F6" />
                        <ArrowRight size={16} className="text-zinc-400 group-hover:text-[#7737F6] transition-colors" />
                      </div>
                      <h3 className="ek-heading text-lg md:text-xl text-black leading-snug">{s.name}</h3>
                      <p className="ek-body text-[14.5px] mt-3">{s.short}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default Services;
